/**
 * FERP Portal - Generic Primitive Mutations Hook
 *
 * Provides CRUD mutations for all 25 FERP primitives.
 * Maps primitive type → GraphQL mutations dynamically.
 *
 * PATTERN: DRY - ONE hook for all primitives
 *
 * MIGRATION: The 25-way switch exists because each primitive has its own generated
 * mutation hook. Future: FormSchema can declare `submission.mutation` metadata to
 * drive mutation routing from schema instead of this switch. Until then, this is
 * the correct routing layer.
 */

import { useTenantId } from '@/auth';
import {
  useCreateWorkflowMutation,
  useCreatePolicyMutation,
  useCreateTaskMutation,
  useCreateActorMutation,
  useCreateEventMutation,
  useCreateAuditMutation,
  useCreateNotificationMutation,
  useCreateQueueMutation,
  useCreateScheduleMutation,
  useCreateConstraintMutation,
  useCreateDependencyMutation,
  useCreateContractMutation,
  useCreateDocumentVersionMutation,
  useArchiveDependencyMutation,
  useArchiveActorMutation,
  useArchiveQueueMutation,
  useArchiveScheduleMutation,
  useArchiveEventsMutation,
  useArchiveAuditMutation,
  useArchiveNotificationMutation,
  useArchiveContractMutation,
  useArchiveDocumentMutation,
} from '@/generated/graphql';
import type { PrimitiveType } from '@/config/primitive-metadata';

/**
 * Generic mutation input type
 */
export interface PrimitiveMutationInput {
  name: string;
  description?: string;
  status?: string;
  active?: boolean;
  enabled?: boolean;
  safetyLevel?: string;
  // Additional fields per primitive
  [key: string]: any;
}

/**
 * Mutations result type
 */
export interface UsePrimitiveMutationsResult {
  create: (input: PrimitiveMutationInput) => Promise<any>;
  update: (id: string, input: PrimitiveMutationInput) => Promise<any>;
  archive: (id: string) => Promise<any>;
  loading: boolean;
}

/**
 * Generic hook for primitive mutations
 *
 * Usage:
 * ```tsx
 * const { create, update, archive, loading } = usePrimitiveMutations('policy');
 *
 * await create({ name: 'New Policy', ... });
 * await update('policy-123', { name: 'Updated Policy', ... });
 * await archive('policy-123');
 * ```
 */
export function usePrimitiveMutations(primitiveType: PrimitiveType): UsePrimitiveMutationsResult {
  const tenantId = useTenantId() ?? '';

  // Get mutation hooks for this primitive type
  const mutations = usePrimitiveMutationHooks(primitiveType);

  const create = async (input: PrimitiveMutationInput) => {
    if (!mutations.create) {
      throw new Error(`Create mutation not available for ${primitiveType}`);
    }

    const result = await mutations.create({
      variables: {
        tenantId,
        // @ts-expect-error Type assignment
        input: {
          ...input,
          tenantId, // Include tenantId in input
        },
      },
    });

    return result.data;
  };

  const update = async (id: string, input: PrimitiveMutationInput) => {
    if (!mutations.update) {
      throw new Error(`Update mutation not available for ${primitiveType}`);
    }

    // @ts-expect-error Not callable
    const result = await mutations.update({
      variables: {
        tenantId,
        id,
        input: {
          ...input,
          tenantId,
        },
      },
    });

    return result.data;
  };

  const archive = async (id: string) => {
    if (!mutations.archive) {
      throw new Error(`Archive mutation not available for ${primitiveType}`);
    }

    const result = await mutations.archive({
      // @ts-expect-error Type assignment
      variables: {
        tenantId,
        id,
      },
    });

    return result.data;
  };

  const loading = mutations.loading;

  return { create, update, archive, loading };
}

/**
 * Maps primitive type to GraphQL mutation hooks
 */
function usePrimitiveMutationHooks(primitiveType: PrimitiveType) {
  // Create mutations
  const [createWorkflow, { loading: loadingCreateWorkflow }] = useCreateWorkflowMutation();
  const [createPolicy, { loading: loadingCreatePolicy }] = useCreatePolicyMutation();
  const [createTask, { loading: loadingCreateTask }] = useCreateTaskMutation();
  const [createActor, { loading: loadingCreateActor }] = useCreateActorMutation();
  const [createEvent, { loading: loadingCreateEvent }] = useCreateEventMutation();
  const [createAudit, { loading: loadingCreateAudit }] = useCreateAuditMutation();
  const [createNotification, { loading: loadingCreateNotification }] = useCreateNotificationMutation();
  const [createQueue, { loading: loadingCreateQueue }] = useCreateQueueMutation();
  const [createSchedule, { loading: loadingCreateSchedule }] = useCreateScheduleMutation();
  const [createConstraint, { loading: loadingCreateConstraint }] = useCreateConstraintMutation();
  const [createDependency, { loading: loadingCreateDependency }] = useCreateDependencyMutation();
  const [createContract, { loading: loadingCreateContract }] = useCreateContractMutation();
  const [createDocument, { loading: loadingCreateDocument }] = useCreateDocumentVersionMutation();

  // Archive mutations
  const [archiveDependency, { loading: loadingArchiveDependency }] = useArchiveDependencyMutation();
  const [archiveActor, { loading: loadingArchiveActor }] = useArchiveActorMutation();
  const [archiveQueue, { loading: loadingArchiveQueue }] = useArchiveQueueMutation();
  const [archiveSchedule, { loading: loadingArchiveSchedule }] = useArchiveScheduleMutation();
  const [archiveEvents, { loading: loadingArchiveEvents }] = useArchiveEventsMutation();
  const [archiveAudit, { loading: loadingArchiveAudit }] = useArchiveAuditMutation();
  const [archiveNotification, { loading: loadingArchiveNotification }] = useArchiveNotificationMutation();
  const [archiveContract, { loading: loadingArchiveContract }] = useArchiveContractMutation();
  const [archiveDocument, { loading: loadingArchiveDocument }] = useArchiveDocumentMutation();

  switch (primitiveType) {
    // DESIGN Phase
    case 'workflow':
      return {
        create: createWorkflow,
        update: null, // TODO: Add when available
        archive: null, // TODO: Add when available
        loading: loadingCreateWorkflow,
      };
    case 'template':
      return {
        create: null, // TODO: Add when available
        update: null,
        archive: null,
        loading: false,
      };
    case 'dependency':
      return {
        create: createDependency,
        update: null,
        archive: archiveDependency,
        loading: loadingCreateDependency || loadingArchiveDependency,
      };
    case 'policy':
      return {
        create: createPolicy,
        update: null,
        archive: null,
        loading: loadingCreatePolicy,
      };
    case 'role':
      return {
        create: null,
        update: null,
        archive: null,
        loading: false,
      };

    // CONFIGURE Phase
    case 'team':
      return {
        create: null,
        update: null,
        archive: null,
        loading: false,
      };
    case 'actor':
      return {
        create: createActor,
        update: null,
        archive: archiveActor,
        loading: loadingCreateActor || loadingArchiveActor,
      };
    case 'validation':
      return {
        create: null,
        update: null,
        archive: null,
        loading: false,
      };
    case 'contract':
      return {
        create: createContract,
        update: null,
        archive: archiveContract,
        loading: loadingCreateContract || loadingArchiveContract,
      };
    case 'constraint':
      return {
        create: createConstraint,
        update: null,
        archive: null,
        loading: loadingCreateConstraint,
      };

    // EXECUTE Phase
    case 'instance':
      return {
        create: null,
        update: null,
        archive: null,
        loading: false,
      };
    case 'task':
      return {
        create: createTask,
        update: null,
        archive: null,
        loading: loadingCreateTask,
      };
    case 'approval':
      return {
        create: null,
        update: null,
        archive: null,
        loading: false,
      };
    case 'document':
      return {
        create: createDocument,
        update: null,
        archive: archiveDocument,
        loading: loadingCreateDocument || loadingArchiveDocument,
      };
    case 'queue':
      return {
        create: createQueue,
        update: null,
        archive: archiveQueue,
        loading: loadingCreateQueue || loadingArchiveQueue,
      };

    // MONITOR Phase
    case 'event':
      return {
        create: createEvent,
        update: null,
        archive: archiveEvents,
        loading: loadingCreateEvent || loadingArchiveEvents,
      };
    case 'notification':
      return {
        create: createNotification,
        update: null,
        archive: archiveNotification,
        loading: loadingCreateNotification || loadingArchiveNotification,
      };
    case 'trace':
      return {
        create: null,
        update: null,
        archive: null,
        loading: false,
      };
    case 'checkpoint':
      return {
        create: null,
        update: null,
        archive: null,
        loading: false,
      };
    case 'transaction':
      return {
        create: null,
        update: null,
        archive: null,
        loading: false,
      };

    // OPTIMIZE Phase
    case 'audit':
      return {
        create: createAudit,
        update: null,
        archive: archiveAudit,
        loading: loadingCreateAudit || loadingArchiveAudit,
      };
    case 'analytics':
      return {
        create: null,
        update: null,
        archive: null,
        loading: false,
      };
    case 'schedule':
      return {
        create: createSchedule,
        update: null,
        archive: archiveSchedule,
        loading: loadingCreateSchedule || loadingArchiveSchedule,
      };
    case 'resource':
      return {
        create: null,
        update: null,
        archive: null,
        loading: false,
      };
    case 'integration':
      return {
        create: null,
        update: null,
        archive: null,
        loading: false,
      };

    default:
      return {
        create: null,
        update: null,
        archive: null,
        loading: false,
      };
  }
}
